<template>
    <Modal :show="visible" fullscreen :closeable="!submitting" @close="close">
        <div class="flex flex-col h-screen">
            <!-- Header -->
            <UiModalHeader :title="lang('invoices::analysis.modal.title')" :closeable="!submitting" @close="close" />

            <!-- Meta bar -->
            <div
                class="flex flex-wrap items-center gap-x-4 gap-y-2 px-5 py-3 border-b border-gray-200 dark:border-zinc-700 bg-gray-50 dark:bg-zinc-800 text-sm"
            >
                <span
                    class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
                    :class="
                        parseLog.status === 'success'
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                    "
                >
                    {{ lang('invoices::analysis.statuses.' + parseLog.status) }}
                </span>
                <span
                    class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
                    :class="providerBadgeClass"
                >
                    {{ providerLabel(parseLog.provider) }}
                </span>
                <span v-if="parseLog.model" class="text-xs font-mono text-gray-500 dark:text-zinc-400">
                    {{ parseLog.model }}
                </span>
                <!--
                <span
                    v-if="parseLog.cost_estimate !== undefined"
                    class="text-xs font-mono text-gray-500 dark:text-zinc-400"
                >
                    ${{ Number(parseLog.cost_estimate).toFixed(4) }}
                </span>
                -->
                <span v-if="parseLog.duration_ms" class="text-xs text-gray-500 dark:text-zinc-400">
                    {{ parseLog.duration_ms }} ms
                </span>
                <span v-if="parseLog.pages" class="text-xs text-gray-500 dark:text-zinc-400">
                    {{ langChoice('invoices::analysis.meta.pages', parseLog.pages, { count: parseLog.pages }) }}
                </span>
            </div>

            <!-- Content -->
            <div class="flex-1 overflow-y-auto p-5">
                <!-- Invoice header fields -->
                <div class="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
                    <div>
                        <label class="block text-xs font-medium text-gray-500 dark:text-zinc-400 mb-1">
                            {{ lang('invoices::analysis.fields.number') }}
                        </label>
                        <input v-model="formData.number" type="text" class="forms__input text-sm w-full" />
                    </div>
                    <div>
                        <label class="block text-xs font-medium text-gray-500 dark:text-zinc-400 mb-1">
                            {{ lang('invoices::analysis.fields.issue_date') }}
                        </label>
                        <input v-model="formData.issue_date" type="date" class="forms__input text-sm w-full" />
                    </div>
                    <div>
                        <label class="block text-xs font-medium text-gray-500 dark:text-zinc-400 mb-1">
                            {{ lang('invoices::analysis.fields.due_date') }}
                        </label>
                        <input v-model="formData.due_date" type="date" class="forms__input text-sm w-full" />
                    </div>
                    <div>
                        <label class="block text-xs font-medium text-gray-500 dark:text-zinc-400 mb-1">
                            {{ lang('invoices::analysis.fields.currency') }}
                        </label>
                        <input v-model="formData.currency" type="text" class="forms__input text-sm w-full" />
                    </div>
                    <div>
                        <label class="block text-xs font-medium text-gray-500 dark:text-zinc-400 mb-1">
                            {{ lang('invoices::analysis.fields.payment_account') }}
                        </label>
                        <input v-model="formData.payment_account" type="text" class="forms__input text-sm w-full" />
                    </div>
                </div>

                <!-- Parties -->
                <div class="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
                    <PartyCard
                        :title="lang('invoices::analysis.sections.supplier')"
                        :party="formData.supplier"
                        :editable="true"
                        @update:party="formData.supplier = $event"
                    />
                    <PartyCard
                        :title="lang('invoices::analysis.sections.client')"
                        :party="formData.client"
                        :editable="true"
                        @update:party="formData.client = $event"
                    />
                </div>

                <!-- Items -->
                <div class="mb-6">
                    <h4 class="mb-2 text-sm font-semibold text-gray-700 dark:text-zinc-300">
                        {{ lang('invoices::analysis.sections.items') }}
                    </h4>
                    <ItemsTable :items="formData.items" :editable="true" @update:items="formData.items = $event" />
                </div>

                <!-- Warnings -->
                <div
                    v-if="props.mappedData._warnings?.length"
                    class="mb-6 rounded-lg border border-amber-300 bg-amber-50 p-4 dark:border-amber-700 dark:bg-amber-900/30"
                >
                    <div class="flex items-start gap-3">
                        <svg
                            class="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-500"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                        >
                            <path
                                fill-rule="evenodd"
                                d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.168 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z"
                                clip-rule="evenodd"
                            />
                        </svg>
                        <div>
                            <p class="text-sm font-medium text-amber-800 dark:text-amber-200">
                                {{
                                    lang(
                                        warningsCorrectedSummary
                                            ? 'invoices::analysis.warnings.summary_corrected'
                                            : 'invoices::analysis.warnings.items_disagree',
                                    )
                                }}
                            </p>
                            <ul class="mt-1 list-disc pl-5 text-xs text-amber-700 dark:text-amber-300">
                                <li v-for="(w, i) in props.mappedData._warnings" :key="i">{{ w }}</li>
                            </ul>
                        </div>
                    </div>
                </div>

                <!-- Summary -->
                <div class="mb-6">
                    <h4 class="mb-2 text-sm font-semibold text-gray-700 dark:text-zinc-300">
                        {{ lang('invoices::analysis.sections.summary') }}
                    </h4>
                    <div class="grid grid-cols-3 gap-4">
                        <div>
                            <label class="block text-xs text-gray-500 dark:text-zinc-400 mb-1">
                                {{ lang('invoices::analysis.fields.total') }}
                            </label>
                            <input
                                v-model.number="formData.summary.total"
                                type="number"
                                class="forms__input text-sm w-full text-right"
                            />
                        </div>
                        <div>
                            <label class="block text-xs text-gray-500 dark:text-zinc-400 mb-1">
                                {{ lang('invoices::analysis.fields.vat') }}
                            </label>
                            <input
                                v-model.number="formData.summary.vat"
                                type="number"
                                class="forms__input text-sm w-full text-right"
                            />
                        </div>
                        <div>
                            <label class="block text-xs text-gray-500 dark:text-zinc-400 mb-1">
                                {{ lang('invoices::analysis.fields.total_vat') }}
                            </label>
                            <input
                                v-model.number="formData.summary.totalVat"
                                type="number"
                                class="forms__input text-sm w-full text-right font-medium"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <!-- Footer -->
            <div class="flex items-center justify-end gap-3 border-t border-gray-200 dark:border-zinc-700 px-5 py-4">
                <Button color="default" :disabled="submitting" @click="close">
                    {{ lang('invoices::analysis.modal.cancel') }}
                </Button>
                <Button color="brand" :disabled="submitting" :loading="submitting" @click="saveAsInvoice">
                    {{ lang('invoices::analysis.modal.save_as_invoice') }}
                </Button>
                <Button color="default" :disabled="submitting" :loading="exporting" @click="saveAndExportIsdoc">
                    {{ lang('invoices::analysis.modal.export_isdoc') }}
                </Button>
            </div>
        </div>
    </Modal>
</template>

<script lang="ts" setup>
import { ref, reactive, computed } from 'vue';
import { lang, langChoice } from '@/lang/composable';
import { router } from '@inertiajs/vue3';
import axios from 'axios';
import PartyCard from './PartyCard.vue';
import ItemsTable from './ItemsTable.vue';

interface MappedData {
    number?: string | null;
    issue_date?: string | null;
    due_date?: string | null;
    currency?: string;
    payment_account?: string | null;
    supplier: Record<string, string | null>;
    client: Record<string, string | null>;
    items: Array<{
        name: string;
        quantity: number;
        price: number;
        priceVat: number;
        vatRate: number;
        description?: string;
    }>;
    summary: {
        total: number;
        totalVat: number;
        vat: number;
    };
    _warnings?: string[];
}

interface ParseLogInfo {
    id: number;
    status: string;
    provider: string;
    model?: string;
    cost_estimate?: number;
    duration_ms?: number;
    pages?: number;
}

const props = defineProps<{
    visible: boolean;
    mappedData: MappedData;
    parseLog: ParseLogInfo;
    createUrl: string;
}>();

const emit = defineEmits<{
    close: [];
}>();

const submitting = ref(false);
const exporting = ref(false);

const formData = reactive<MappedData>({
    number: props.mappedData.number ?? null,
    issue_date: props.mappedData.issue_date ?? null,
    due_date: props.mappedData.due_date ?? null,
    currency: props.mappedData.currency ?? 'CZK',
    payment_account: props.mappedData.payment_account ?? null,
    supplier: { ...props.mappedData.supplier },
    client: { ...props.mappedData.client },
    items: props.mappedData.items.map((i) => ({ ...i })),
    summary: { ...props.mappedData.summary },
});

const providerLabel = (provider: string): string => {
    return lang('invoices::analysis.providers.' + provider) || provider;
};

const warningsCorrectedSummary = computed(() => {
    return props.mappedData._warnings?.some((w: string) => w.includes('corrected')) ?? false;
});

const providerBadgeClass = computed(() => {
    switch (props.parseLog.provider) {
        case 'vision':
            return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
        case 'isdoc':
            return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200';
        default:
            return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
    }
});

const close = () => {
    if (!submitting.value) {
        emit('close');
    }
};

const saveAsInvoice = async () => {
    submitting.value = true;

    try {
        const response = await axios.post(props.createUrl, {
            parse_log_id: props.parseLog.id,
            mapped_data: formData,
        });

        if (response.data.redirect) {
            router.visit(response.data.redirect);
        }
    } catch (error: unknown) {
        console.error('Failed to create invoice:', error);
    } finally {
        submitting.value = false;
    }
};

const saveAndExportIsdoc = async () => {
    exporting.value = true;

    try {
        const response = await axios.post(props.createUrl, {
            parse_log_id: props.parseLog.id,
            mapped_data: formData,
        });

        if (response.data.invoice_id) {
            window.location.href = route('invoices.admin.isdoc', { id: response.data.invoice_id });
        }
    } catch (error: unknown) {
        console.error('Failed to create invoice for ISDOC export:', error);
    } finally {
        exporting.value = false;
    }
};
</script>
